// React & core
import { useEffect, useMemo, useRef, useState } from "react";

// Animation / Routing
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";

// Icons
import {
  X,
  Mail,
  Building2,
  IdCard,
  Copy,
  Briefcase,
  User,
  Calendar,
  CheckCircle,
  Layers,
  MailCheck,
  PhoneCall,
  Check,
  Trophy,
  XCircle,
  Pencil,
  ThumbsUp,
  ThumbsDown,
  Puzzle,
  FileText,
  Plus,
  TimerIcon,
  FileTextIcon,
  MessageCircleCodeIcon,
  MessageCircle,
  PhoneCallIcon,
  FolderKanban,
  BadgeDollarSign,
  ALargeSmall,
  Send,
  PackageSearch,
  Slack,
  CalendarSync,
  MailCheckIcon,
  PhoneIcon,
  Warehouse,
  MapPinHouse,
  MapMinus,
  BadgeAlert,
  DollarSign,
  ChartNoAxesCombined,
  Key,
} from "lucide-react";

// Custom hooks & UI
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import Swal from "sweetalert2";
import { Alert, Snackbar } from "@mui/material";
import CommentsSection from "../../../../../component/comment";
import EmployeeDirectoryAddUpdate from "../../../../organization/emplyee-directory/add-update";
import LostDeal from "../../../../mainDeals/deals/deal/lost-deal/lost-form-deal";
import WonDeal from "../../../../mainDeals/deals/deal/won-deal/won-form-deal";
import DealQuickView from "../../../../mainDeals/deals/deal/popup";
import AddActivity from "../../../../../component/modals/add-activity";
import { CustomDropdown } from "../../../../../component/ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../../../../../component/ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../../../../../component/ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../../../../../component/ui/dropdown/CustomDropdownMenuList";
import ProjectQuickView from "../../../../mainProjects/projects/project/popup";
import { PopupTimeline } from "../../../../../component/ui/timeline";
import {
  DynamicTable,
  FieldRow,
} from "../../../../../component/Popup-comp";
import NewCardIcon from "../../../../../component/icons/action-card-icon";
import InvoiceQuickView from "../../../../../component/invoice/InvoiceQuickView";
import Icon from "../../../../../component/icons/inner-pages-icons";

const tabs = [
  { key: "timeline", label: "Timeline", icon: <TimerIcon size={16} /> },
  { key: "summary", label: "Summary", icon: <User size={16} /> },
  { key: "deals", label: "Deals", icon: <FileTextIcon size={16} /> },
  {
    key: "client_projects",
    label: "Projects",
    icon: <FolderKanban size={16} />,
  },
  {
    key: "transactions",
    label: "Transactions",
    icon: <BadgeDollarSign size={16} />,
  },
  {
    key: "comments",
    label: "Comments",
    icon: <MessageCircleCodeIcon size={16} />,
  },
  { key: "call", label: "Calls", icon: <PhoneCallIcon size={16} /> },
  { key: "emails", label: "Emails", icon: <Mail size={16} /> },
  { key: "sms", label: "Sms", icon: <MessageCircle size={16} /> },
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-[6px]  px-[9px] py-[3px] text-[11px] font-medium",
        tones[tone],
      )}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}



const statusColor = {
  completed: { background: "#E6F9F0", color: "#0F9D58" }, // emerald green
  on_hold: { background: "#E8F0FE", color: "#1A73E8" }, // royal blue
  pending: { background: "#fff3e0", color: "#e65100" }, // deep orange
  in_progress: { background: "#F3E8FF", color: "#9333EA" }, // violet
  revision: { background: "#E0F7FA", color: "#00838F" }, // cyan / teal
  "no answer update 2": { background: "#f3f4f6", color: "#374151" }, // cyan / teal
  "wrong number": { background: "#fee2e2", color: "#b91c1c" }, // cyan / teal
  "call back": { background: "#fffbeb", color: "#78350f" }, // cyan / teal
  "not interested": { background: "#fee2e2", color: "#b91c1c" }, // cyan / teal
  "do not contact": { background: "#f9fafb", color: "#374151" }, // cyan / teal
  duplicate: { background: "#ede9fe", color: "#5b21b6" },
};

export default function AccountQuickView({
  open = true,
  onClose = () => { },
  onError = () => { },
  onSubmit = () => { },
  data,
  ProductList = [],
  PackageList = [],
  DealStages = [],
  ClientList = [],
  UserList = [],
}) {
  const [contact, setcontact] = useState({});
  const [activeTab, setActiveTab] = useState("timeline");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(contact?.contactstatus);
  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  const [clientDeals, setClientDeals] = useState([]);
  const [alertData, setAlertData] = useState({ isShow: false });
  const [changeModal, setChangeModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);

  const [projPopup, setProjPopup] = useState(false);
  const [projData, setProjData] = useState({});
  const [usersList, setUsers] = useState([]);

  const [invoicePopup, setInvoicePopup] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceProjectKey, setInvoiceProjectKey] = useState(null);

  const handleClose = () => {
    setAlertData({ isShow: false });
  };

  const GetData = async (id) => {
    let apiData = JSON.stringify({
      userid: id,
    });

    try {
      let call = await apiCall({
        method: "get",
        url: `accounts/get-account/${id}`,
        data: apiData,
      });
      let ContactData = call?.data;

      console.log("ContactData", ContactData);

      let activity = ContactData?.recent_activity?.map((item) => {
        const dateObj = new Date(item.time);
        const formattedDate = dateObj
          .toISOString()
          .slice(0, 16)
          .replace("T", " ");
        // This gives "YYYY-MM-DD HH:MM"

        return {
          ts: formattedDate,
          text: item.data,
        };
      });

      const sdata = {
        // ---------- SUMMARY TAB ----------
        id: ContactData?.userid,
        name: ContactData?.name,
        email: ContactData?.email,
        phone: ContactData?.phone,
        source: ContactData?.product_name,
        page_source: ContactData?.page_source,
        brandurl: ContactData?.brand?.brandurl,
        created_at: ContactData?.created_at,
        activities: ContactData?.activities,

        // ---------- CUSTOMER INFORMATION TAB ----------
        altemail: ContactData?.altemail,
        alternatephone: ContactData?.alternatephone,
        usertype: ContactData?.usertype,
        country: ContactData?.country,
        tagline: ContactData?.tagline,
        userimg: ContactData?.userimg,
        companyname: ContactData?.companyname,
        address: ContactData?.address,
        city: ContactData?.city,
        state: ContactData?.state,
        zipcode: ContactData?.zipcode,
        verified: ContactData?.verified,
        verifykey: ContactData?.verifykey,
        badge: ContactData?.badge,
        view_tutorial: ContactData?.view_tutorial,
        active: ContactData?.active,
        theme: ContactData?.theme,
        AssignedTo: ContactData?.assigned_users,
        notify: ContactData?.notify,
        teamleadid: ContactData?.teamleadid,
        revoke_user: ContactData?.revoke_user,
        is_private: ContactData?.is_private,
        is_private_assignee: ContactData?.is_private_assignee,
        signupdate: ContactData?.signupdate,
        signuptime: ContactData?.signuptime,

        // ---------- RELATED DATA ----------
        transactions: ContactData?.transactions ?? [],
        client_deals: ContactData?.client_deals ?? [],
        client_projects: ContactData?.client_projects ?? [],
        stats: ContactData?.stats ?? [],

        /// project details
      };

      setStatus(ContactData?.contactstatus);
      setcontact(sdata);
      setIsLoading(true);
      setClientDeals(sdata.client_deals || []);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `users/agents-list`,
      });

      console.log("Users API Response ===>", res);
      const newUsers = res?.agents || res?.customers || res?.data || [];
      setUsers(newUsers);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onSuccess = (message) => {
    // if (onSubmit) onSubmit(message); // ✅ call parent prop
    setAlertData({ isShow: true, type: "success", message: message });
    GetData(contact?.id);
    setChangeModal(false);
    setShowAddDealModal(false);
  };

  useEffect(() => {
    if (activeTab === "timeline") {
      GetData(data?.client_id || data?.userid);
    }
  }, [activeTab, data]);

  const onActivitySuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    GetData(contact?.id);
    setShowActivityModal(false);
  };

  

 



  const T = {
    bg: "#EDE8E1",
    sidebar: "#1a1726",
    sidebarText: "rgba(255,255,255,0.45)",
    sidebarActive: "#FFFFFF",
    sidebarActiveBg: "rgba(99,102,241,0.15)",
    sidebarHover: "rgba(255,255,255,0.05)",
    sidebarAccent: "#818cf8",
    card: "#FFFFFF",
    cardBorder: "rgba(0,0,0,0.10)",
    cardHover: "#FAF8F6",
    text: "#1a1520",
    textSecondary: "#5c5466",
    textMuted: "#908892",
    border: "rgba(0,0,0,0.08)",
    accent: "#6366f1",
    accentLight: "#EEF0FF",
    accentText: "#4F46E5",
    inputBg: "#F3EFE9",
    inputBorder: "rgba(0,0,0,0.1)",
    modalBg: "#FFFFFF",
    rowHover: "#F5F1EC",
    selectedRow: "#EEEAFF",
    selectedBorder: "#6366f1",
    shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
    shadowLg: "0 8px 30px rgba(0,0,0,0.08)",
  };

  const css = {
    app: {
      display: "flex",
      height: "100vh",
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      background: T.bg,
      color: T.text,
      overflow: "hidden",
    },
    sidebar: {
      width: 250,
      background: T.sidebar,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      overflow: "hidden",
    },
    main: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    card: {
      background: T.card,
      border: `1px solid ${T.cardBorder}`,
      borderRadius: 12,
      padding: 16,
      boxShadow: T.shadow,
    },
    btn: {
      padding: "7px 14px",
      borderRadius: 8,
      border: `1px solid ${T.border}`,
      background: T.card,
      color: T.textSecondary,
      cursor: "pointer",
      fontSize: 13,
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontFamily: "inherit",
      transition: "all 0.15s",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },
    btnPrimary: {
      padding: "8px 18px",
      borderRadius: 8,
      border: "none",
      background: "linear-gradient(92deg,#0057FB 1.54%,#003495 114.12%)",
      color: "#fff",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontFamily: "inherit",
      boxShadow: "0 2px 10px rgba(99,102,241,0.3)",
    },
    input: {
      background: T.inputBg,
      border: `1px solid ${T.inputBorder}`,
      borderRadius: 8,
      padding: "9px 12px",
      color: T.text,
      fontSize: 13,
      outline: "none",
      fontFamily: "inherit",
      width: "100%",
    },
    select: {
      background: T.inputBg,
      border: `1px solid ${T.inputBorder}`,
      borderRadius: 8,
      padding: "6px 12px",
      color: T.text,
      fontSize: 13,
      outline: "none",
      fontFamily: "inherit",
      cursor: "pointer",
    },
    badge: (color) => ({
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 9px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: color + "14",
      color: color,
      letterSpacing: "0.3px",
    }),
    pill: (active) => ({
      padding: "5px 12px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: active ? 600 : 400,
      border: active
        ? "1px solid rgba(99,102,241,0.35)"
        : `1px solid ${T.border}`,
      background: active ? T.accentLight : T.card,
      color: active ? T.accentText : T.textMuted,
      cursor: "pointer",
      fontFamily: "inherit",
      boxShadow: active ? "none" : "0 1px 2px rgba(0,0,0,0.03)",
    }),
  };

  const activityIcon = (type) =>
    ({
      call: "phone",
      phone: "phone",
      email: "mail",
      sms: "sms",
      consultation: "consultation",
      default: "default",

      note: "note",
      meeting: "calendar",
      "follow-up": "calendar",
      followup: "calendar",
      stage: "arrowRight",
      voicemail: "mic",
      chat: "sms",
      comment: "sms",
      demo: "demo",
      messasge: "sms",
      presentation: "presentation",
      assign: "assign",
      unassign: "unassign",
      file: "file",
      brief: "brief",
    })[type] || "check";
  const activityColor = (type) =>
    ({
      call: "#3b82f6", // blue
      email: "#8b5cf6", // violet
      sms: "#06b6d4", // cyan
      consultation: "#f59e0b", // amber
      note: "#f59e0b", // amber
      meeting: "#10b981", // emerald
      stage: "#6366f1", // indigo
      "follow-up": "#6366f1", // indigo
      voicemail: "#ef4444", // red

      chat: "#0ea5e9", // sky blue
      presentation: "#14b8a6", // teal
      demo: "#a855f7", // purple
      default: "#a855f7", // purple
      messasge: "#f43f5e", // rose

      // remaining icons (added)
      phone: "#3b82f6", // blue
      comment: "#0ea5e9", // sky
      followup: "#6366f1", // indigo
      assign: "#22c55e", // green
      unassign: "#ef4444", // red
      file: "#eab308", // slate
      brief: "#eab308", // yellow
    })[type] || "#6b7280";

  ////////////// fake data for calls //////////////
  const refreshLead = {
    id: contact?.id,
    leadName: contact?.name,
    phone: contact?.phone,
    activities: [
      // {
      //   id: 1,
      //   type: "call",
      //   summary: "Initial Discussion Call",
      //   date: "2024-07-01T10:30:00",
      //   agent: "Hendry",
      //   duration: "12m 45s",
      //   details:
      //     "Discussed product requirements and pricing structure. Client showed interest in premium plan.",
      // }
    ],
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  const fmtDateTime = (iso) => `${fmtDate(iso)} ${fmtTime(iso)}`;


  const MAX_VISIBLE_TABS = 6;

  const getOrderedTabs = () => {
    if (!tabs) return [];

    const activeIndex = tabs.findIndex((t) => t.key === activeTab);

    // If active tab already in first 6 → no change
    if (activeIndex < MAX_VISIBLE_TABS) {
      return tabs;
    }

    // Otherwise swap active tab with last visible tab
    const newTabs = [...tabs];
    const activeItem = newTabs.splice(activeIndex, 1)[0];
    newTabs.splice(MAX_VISIBLE_TABS - 1, 0, activeItem);

    return newTabs;
  };

  const orderedTabs = getOrderedTabs();

  const visibleTabs = orderedTabs.slice(0, MAX_VISIBLE_TABS);
  const extraTabs = orderedTabs.slice(MAX_VISIBLE_TABS);
  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 !pr-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.aside
              ref={containerRef}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative mt-0 mr-0 mb-auto ml-0 flex h-full p-[28px] pb-0 w-[810px] max-w-[98vw] flex-col overflow-hidden rounded-3xl rounded-r-none  border border-solid border-slate-200 bg-[#ffffff] shadow-xl"
              aria-modal
              role="dialog"
            >
              {isLoading ? (
                <>
                  {/* Header */}
                  <div className=" bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                    <div className="flex flex-wrap items-center justify-between gap-3 w-full pb-4 ">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex items-center justify-center size-12  bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 rounded-full overflow-hidden">
                          <p className="capitalize font-semi-bold text-base text-black">
                            {contact?.name
                              ?.split(" ")
                              ?.map((n, i) => n.charAt(0))}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h2 className="text-xl font-semibold text-slate-900">
                              {contact?.name}
                            </h2>

                            <Badge
                              tone={contact?.active == "1" ? "green" : "red"}
                            >
                              {contact?.active == "1" ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <MailCheck className="size-4" /> {contact?.email}
                            </span>

                            <span className="inline-flex items-center gap-1">
                              <PhoneCall className="size-4" /> {contact?.phone}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                          onClick={onClose}
                          aria-label="Close"
                        >
                          <X className="size-4" /> Exit
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden  pt-0 w-full">
                    {/* Left: main content */}
                    <div className="col-span-12 flex min-h-0 flex-col gap-4">
                      {/* Top stats */}
                      <div className="flex flex-wrap items-center gap-2 justify-between">
                        {/* <div>
                          <PopupAssigned
                            AssignedUsers={contact?.AssignedTo}
                            client={contact?.id}
                            apiLoadonSuccess={() =>
                              GetData(data?.client_id || data?.userid)
                            }
                          />
                        </div> */}

                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            // marginTop: 14,
                            flexWrap: "wrap",
                            textTransform: "capitalize"
                          }}
                        >
                          {contact?.stats?.map(
                            (ele, idx) =>
                              ele?.value !== "-" &&
                              ele?.value !== null && (
                                <div
                                  style={{
                                    padding: "8px 14px",
                                    background: "#F3EFE9",
                                    borderRadius: 10,
                                    border: `1px solid rgba(0,0,0,0.08)`,
                                  }}
                                >
                                  <div style={{ fontSize: 10, color: "#908892" }}>
                                    {ele?.key?.includes("_")
                                      ? ele?.key
                                        ?.split("_")
                                        .map((word) => ` ${word}`)
                                      : ele?.key}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 15,
                                      fontWeight: 700,
                                      marginTop: 3,
                                      color: "#1a1520",
                                    }}
                                  >
                                    {ele?.value}
                                  </div>
                                </div>
                              ),
                          )}
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            className="inline-flex items-center gap-1.5 tracking-normal rounded-lg bg-[linear-gradient(92deg,#0057FB_1.54%,#003495_114.12%)] 
                          hover:bg-[linear-gradient(92deg,#0066FF_1.54%,#0045AA_114.12%)] px-[18px] py-2 text-[13px] font-semibold text-white"
                            onClick={() => setShowActivityModal(true)}
                            aria-label="Close"
                          >
                            <Plus className="size-[14px]" /> Add Follow Up
                          </button>

                          <button
                            className="inline-flex items-center gap-1.5 rounded-lg border border-solid border-[rgba(0,0,0,0.1)] bg-[rgb(243,239,233)]  px-[18px] py-2 text-[13px] font-semibold text-[rgb(26,21,32)] hover:bg-slate-50"
                            onClick={() => setShowAddDealModal(true)}
                          >
                            <Briefcase className="size-[14px]" /> Add Deal
                          </button>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="border-y border-solid pt-1 border-slate-200 flex items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-[3px]">
                          {/* Visible Tabs */}
                          {visibleTabs?.map((t) => (
                            <button
                              key={t.key}
                              onClick={() => setActiveTab(t.key)}
                              className={classNames(
                                "flex items-center gap-[6px] px-[16px] py-[11px] text-[13px] font-normal border-b-2 transition-all duration-200",
                                activeTab === t.key
                                  ? "border-blue-600 text-blue-600 font-semibold"
                                  : "border-transparent text-slate-500 hover:text-slate-800",
                              )}
                            >
                              {t.icon}
                              {t.label}
                            </button>
                          ))}

                          {/* More Dropdown */}
                          {extraTabs.length > 0 && (
                            <CustomDropdown position="bottom-right">
                              <CustomDropdownButton
                                arrow={false}
                                className={classNames(
                                  "flex items-center gap-[6px] !px-[8px] !py-[4px] text-[13px] font-normal !border-0 transition-all duration-200",
                                  extraTabs.some((t) => t.key === activeTab)
                                    ? "border-blue-600 text-blue-600 font-semibold"
                                    : "border-transparent text-slate-500 hover:text-slate-800",
                                )}
                              >
                                <svg
                                  width={18}
                                  height={18}
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <circle cx="5" cy="12" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="19" cy="12" r="2" />
                                </svg>
                              </CustomDropdownButton>

                              <CustomDropdownMenu>
                                {extraTabs.map((t) => (
                                  <CustomDropdownMenuList
                                    key={t.key}
                                    onClick={() => setActiveTab(t.key)}
                                    className="hover:bg-slate-100"
                                  >
                                    <div className="flex items-center gap-2">
                                      {t.icon}
                                      <span>{t.label}</span>
                                    </div>
                                  </CustomDropdownMenuList>
                                ))}
                              </CustomDropdownMenu>
                            </CustomDropdown>
                          )}
                        </div>
                      </div>

                      <div className="h-full overflow-auto rounded-2xl py-2">
                        {activeTab === "timeline" && (
                          // <div className="px-2 w-full h-full rounded-md">
                          //   {contact?.activities?.map((act, i) => {
                          //     const config =
                          //       activityConfig[act.type] || activityConfig.note;
                          //     // const Icon = config.icon;
                          //     // const color = config.color;
                          //     return (
                          //       <div
                          //         key={act.id}
                          //         style={{
                          //           display: "flex",
                          //           gap: 12,
                          //           marginBottom: 16,
                          //         }}
                          //       >
                          //         <div
                          //           style={{
                          //             display: "flex",
                          //             flexDirection: "column",
                          //             alignItems: "center",
                          //           }}
                          //         >
                          //           <div
                          //             style={{
                          //               width: 34,
                          //               height: 34,
                          //               borderRadius: "50%",
                          //               background:
                          //                 activityColor(
                          //                   act?.icon?.toLowerCase(),
                          //                 ) + "12",
                          //               display: "flex",
                          //               alignItems: "center",
                          //               justifyContent: "center",
                          //               color: activityColor(
                          //                 act?.icon?.toLowerCase(),
                          //               ),
                          //               flexShrink: 0,
                          //             }}
                          //           >
                          //             {I(
                          //               activityIcon(act?.icon?.toLowerCase()),
                          //               15,
                          //             )}
                          //           </div>
                          //           {i < contact.activities.length - 1 && (
                          //             <div
                          //               style={{
                          //                 width: 1,
                          //                 flex: 1,
                          //                 background: T.border,
                          //                 marginTop: 4,
                          //               }}
                          //             />
                          //           )}
                          //         </div>
                          //         <div style={{ flex: 1, paddingBottom: 4 }}>
                          //           <div
                          //             style={{
                          //               display: "flex",
                          //               justifyContent: "space-between",
                          //               alignItems: "center",
                          //               marginBottom: 2,
                          //             }}
                          //           >
                          //             <span
                          //               style={{
                          //                 fontSize: 13,
                          //                 fontWeight: 600,
                          //                 color: T.text,
                          //               }}
                          //               className="capitalize"
                          //             >
                          //               {act?.logcat}
                          //             </span>
                          //             <span
                          //               style={{
                          //                 fontSize: 11,
                          //                 color: T.textMuted,
                          //               }}
                          //             >
                          //               {act?.logtime}
                          //             </span>
                          //           </div>
                          //           <div
                          //             style={{
                          //               fontSize: 12,
                          //               color: T.textMuted,
                          //               marginBottom: 4,
                          //             }}
                          //           >
                          //             {act?.logtype} · {act?.user?.name} ·{" "}
                          //             {moment(
                          //               act?.logtime,
                          //               "DD-MMM-YYYY hh:mm:ss A",
                          //             ).fromNow()}
                          //           </div>
                          //           <div
                          //             style={{
                          //               fontSize: 12,
                          //               color: T.textSecondary,
                          //               lineHeight: 1.6,
                          //             }}
                          //           >
                          //             {act.log}
                          //           </div>
                          //         </div>
                          //       </div>
                          //     );
                          //   })}
                          //   {contact?.activities?.length === 0 && (
                          //     <div
                          //       style={{
                          //         textAlign: "center",
                          //         color: "#908892",
                          //         padding: 40,
                          //       }}
                          //     >
                          //       No activities logged yet
                          //     </div>
                          //   )}
                          // </div>

                          <PopupTimeline timeline={contact?.activities} />
                        )}

                        {activeTab === "summary" && (
                          <>
                            <div>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Account Information
                              </div>

                              <div className="grid gap-6 md:grid-cols-2 w-full px-[10px]">
                                <FieldRow
                                  label="Full Name"
                                  value={contact?.name}
                                  LabelIcon={ALargeSmall}
                                />
                                <FieldRow
                                  label="Email"
                                  value={contact?.email}
                                  LabelIcon={Send}
                                  isCapitalize={false}
                                  isCopyable={true}
                                />

                                <FieldRow
                                  label="Phone"
                                  value={contact?.phone}
                                  LabelIcon={PhoneCall}
                                />
                                <FieldRow
                                  label="Product"
                                  value={contact?.source}
                                  LabelIcon={PackageSearch}
                                />

                                <FieldRow
                                  label="Brand Name"
                                  value={contact?.page_source}
                                  LabelIcon={Slack}
                                  isLink={true}
                                  LinkValue={contact?.brandurl}
                                />
                                <FieldRow
                                  label="Date"
                                  value={contact?.signupdate}
                                  LabelIcon={CalendarSync}
                                />

                                {/* <FieldRow label="First Name" value={contact?.email} LabelIcon={Send} />
                              <FieldRow label="First Name" value={contact?.email} LabelIcon={Send} /> */}
                              </div>
                            </div>

                            <div className="mt-[12px]">
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Contact Information
                              </div>

                              <div className="grid gap-6 md:grid-cols-2 w-full px-[10px]">
                                <FieldRow
                                  label="Alternate Email"
                                  value={contact?.altemail}
                                  LabelIcon={MailCheckIcon}
                                  isCapitalize={false}
                                />
                                <FieldRow
                                  label="Alternate Phone"
                                  value={contact?.alternatephone}
                                  LabelIcon={PhoneIcon}
                                  isCapitalize={false}
                                />

                                <FieldRow
                                  label="Country"
                                  value={contact?.country}
                                  LabelIcon={Warehouse}
                                />
                                <FieldRow
                                  label="State"
                                  value={contact?.state}
                                  LabelIcon={MapPinHouse}
                                />

                                <FieldRow
                                  label="City"
                                  value={contact?.city}
                                  LabelIcon={MapMinus}
                                />
                                <FieldRow
                                  label="Company"
                                  value={contact?.companyname}
                                  LabelIcon={Building2}
                                />

                                <FieldRow
                                  label="Badge"
                                  value={contact?.badge}
                                  LabelIcon={BadgeAlert}
                                />
                                {/* <FieldRow label="First Name" value={contact?.email} LabelIcon={Send} /> */}
                              </div>
                            </div>
                          </>
                        )}

                        {activeTab === "transactions" && (
                          <DynamicTable
                            data={contact?.transactions}
                            rowKey="transactionid"
                            // rowUrlBuilder={(row) => {
                            //   if (row?.projectkey) {
                            //     setInvoicePopup(true);
                            //     // setInvoiceData(row?.invoice_details);
                            //     setInvoiceProjectKey(row?.projectkey)
                            //   }
                            // }}
                            onRowClick={(row) => {
                              if (row?.projectkey) {
                                setInvoicePopup(true);
                                setInvoiceProjectKey(row?.projectkey)
                              }
                            }}
                            columns={[
                              {
                                key: "transactionkey",
                                label: "Transaction Key",
                                icon: Key, // icon for header
                                copy: true,
                                render: (row) => row.transactionkey ?? "-",
                              },
                              {
                                key: "amount",
                                label: "Amount",
                                icon: DollarSign, // icon for header
                                render: (row) => `$${row.amount ?? "-"}`, // prepend $ sign
                              },
                              {
                                key: "transactiontime",
                                label: "Time",
                                icon: CheckCircle, // icon for header
                                render: (row) => row.transactiontime ?? "-",
                              },
                              {
                                key: "transactionstatus",
                                label: "Status",
                                icon: ChartNoAxesCombined, // icon for header
                                render: (row) => {
                                  const color =
                                    row.transactionstatus === "success"
                                      ? "green"
                                      : row.transactionstatus === "pending"
                                        ? "blue"
                                        : "red";

                                  return (
                                    <div
                                      className={`inline-flex items-center  gap-1 rounded-[6px]  px-[9px] py-[3px] text-xs font-medium ${color === "green"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : color === "blue"
                                          ? "bg-blue-100 text-blue-700"
                                          : "bg-rose-100 text-rose-700"
                                        }`}
                                    >
                                      {row.transactionstatus ?? "-"}
                                    </div>
                                  );
                                },
                              },
                            ]}
                          />
                        )}
                        {activeTab === "deals" && (
                          <div className="">
                            {contact?.client_deals?.length > 0 ? (
                              clientDeals.map((deal, i) => (
                                <DealCard
                                  key={i}
                                  userid={contact?.id}
                                  deal={deal}
                                  ProductList={ProductList}
                                  PackageList={PackageList}
                                  DealStages={DealStages}
                                  ClientList={ClientList}
                                  UserList={UserList}
                                  onSubmit={onSuccess}
                                  onError={onError}
                                  onStatusChange={(dealId, newStatus) => {
                                    // UPDATE STATE IMMEDIATELY
                                    setClientDeals((prevDeals) =>
                                      prevDeals.map((d) =>
                                        d.dealid === dealId
                                          ? {
                                            ...d,
                                            current_stage: {
                                              ...d.current_stage,
                                              dealstatus: newStatus,
                                            },
                                          } // new object
                                          : d,
                                      ),
                                    );
                                  }}
                                />
                              ))
                            ) : (
                              <div className="text-sm text-slate-500">
                                No deals found for this contact.
                              </div>
                            )}
                          </div>
                        )}
                        {activeTab === "client_projects" && (
                          <>
                            <DynamicTable
                              data={contact?.client_projects}
                              rowKey="projectid"
                              // statusColor={statusColor}
                              rowUrlBuilder={(row) =>
                                `/lead-account/detail/${row.customer}/project/${row.projectkey}`
                              }
                              columns={[
                                {
                                  key: "invoiceid",
                                  label: "Invoice ID",
                                  copy: true,
                                  icon: Layers,
                                },
                                {
                                  key: "projectcost",
                                  label: "Project Cost",
                                  icon: DollarSign,
                                },
                                {
                                  key: "startdate",
                                  label: "Start Date",
                                  icon: Calendar,
                                },
                                {
                                  key: "status",
                                  label: "Status",
                                  icon: ChartNoAxesCombined,
                                  render: (row) => (
                                    <div
                                      className={`inline-flex items-center  rounded-[6px] tracking-tighter px-[9px] py-[3px] text-xs font-medium transition-all duration-200 
                                      `}
                                      style={
                                        statusColor[row?.status?.toLowerCase()] || {
                                          background: "#f5f5f5",
                                          color: "#616161",
                                        }
                                      }
                                    >
                                      {row?.status
                                        ? row.status
                                          .split("_")
                                          .map(
                                            (word) =>
                                              word.charAt(0).toUpperCase() +
                                              word.slice(1),
                                          )
                                          .join(" ")
                                        : "-"}
                                    </div>
                                  ),
                                },
                              ]}
                            />

                            {projPopup && (
                              <ProjectQuickView
                                onClose={() => {
                                  setProjPopup(false);
                                  setProjData({});
                                }}
                                data={projData}
                              />
                            )}
                          </>
                        )}

                        {activeTab === "comments" && (
                          <CommentsSection
                            type="contact"
                            id={contact?.id}
                            apiBaseUrl="contact"
                            setAlertData={setAlertData}
                          />
                        )}

                        {activeTab === "call" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Make a Call
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  marginBottom: 16,
                                }}
                              >
                                <input
                                  style={{ ...css.input, flex: 1 }}
                                  value={refreshLead.phone}
                                  readOnly
                                />
                                <button style={css.btnPrimary}>
                                  {/* {I("phone", 14)} Call Now */}
                                  <Icon name="phone" size={14} /> Call Now
                                </button>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                                marginBottom: 8,
                              }}
                            >
                              Call History
                            </div>
                            {refreshLead.activities
                              .filter(
                                (a) =>
                                  a.type === "call" || a.type === "voicemail",
                              )
                              .map((call) => (
                                <div
                                  key={call.id}
                                  style={{
                                    ...css.card,
                                    display: "flex",
                                    gap: 12,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 34,
                                      height: 34,
                                      borderRadius: "50%",
                                      background:
                                        activityColor(call.type) + "12",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: activityColor(call.type),
                                      flexShrink: 0,
                                    }}
                                  >
                                    {/* {I(activityIcon(call.type), 15)} */}
                                     <Icon name="call" size={15} />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        marginBottom: 2,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: T.text,
                                        }}
                                      >
                                        {call.summary}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 11,
                                          color: T.textMuted,
                                        }}
                                      >
                                        {fmtDateTime(call.date)}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: T.textMuted,
                                        marginBottom: 4,
                                      }}
                                    >
                                      {call.agent} · {call.duration}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: T.textSecondary,
                                      }}
                                    >
                                      {call.details}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            {refreshLead.activities.filter(
                              (a) =>
                                a.type === "call" || a.type === "voicemail",
                            ).length === 0 && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: T.textMuted,
                                    padding: 40,
                                  }}
                                >
                                  No call history
                                </div>
                              )}
                          </div>
                        )}

                        {activeTab === "emails" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Compose Email
                              </div>
                              <input
                                placeholder="Subject..."
                                style={{ ...css.input, marginBottom: 8 }}
                              />
                              <textarea
                                placeholder="Type your email message..."
                                style={{
                                  ...css.input,
                                  minHeight: 120,
                                  resize: "vertical",
                                  marginBottom: 8,
                                }}
                              />
                              <button style={css.btnPrimary}>
                                {/* {I("send", 14)} Send Email */}
                                 <Icon name="send" size={14} /> Send Email
                              </button>
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                                marginBottom: 8,
                              }}
                            >
                              Email History
                            </div>
                            {refreshLead.activities
                              .filter((a) => a.type === "email")
                              .map((email) => (
                                <div key={email.id} style={{ ...css.card }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: T.text,
                                      }}
                                    >
                                      {email.summary}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: T.textMuted,
                                      }}
                                    >
                                      {fmtDateTime(email.date)}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: T.textMuted,
                                      marginBottom: 4,
                                    }}
                                  >
                                    From: {email.agent}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: T.textSecondary,
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {email.details}
                                  </div>
                                </div>
                              ))}
                            {refreshLead.activities.filter(
                              (a) => a.type === "email",
                            ).length === 0 && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: T.textMuted,
                                    padding: 40,
                                  }}
                                >
                                  No email history
                                </div>
                              )}
                          </div>
                        )}

                        {activeTab === "sms" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Send SMS
                              </div>
                              <textarea
                                placeholder="Type your message..."
                                style={{
                                  ...css.input,
                                  minHeight: 80,
                                  resize: "vertical",
                                  marginBottom: 8,
                                }}
                              />
                              <button style={css.btnPrimary}>
                                {/* {I("send", 14)} Send SMS */}
                                <Icon name="send" size={14} /> Send SMS
                              </button>
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                                marginBottom: 8,
                              }}
                            >
                              SMS History
                            </div>
                            {refreshLead.activities
                              .filter((a) => a.type === "sms")
                              .map((sms) => (
                                <div key={sms.id} style={{ ...css.card }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: T.text,
                                      }}
                                    >
                                      {sms.summary}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: T.textMuted,
                                      }}
                                    >
                                      {fmtDateTime(sms.date)}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: T.textMuted,
                                      marginBottom: 4,
                                    }}
                                  >
                                    {sms.agent}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: T.textSecondary,
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {sms.details}
                                  </div>
                                </div>
                              ))}
                            {refreshLead.activities.filter(
                              (a) => a.type === "sms",
                            ).length === 0 && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: T.textMuted,
                                    padding: 40,
                                  }}
                                >
                                  No SMS history
                                </div>
                              )}
                          </div>
                          // <TabPlaceholder
                          //   icon={MessageSquare}
                          //   title="SMS"
                          //   message="SMS communication history is under development."
                          // />
                        )}
                      </div>
                    </div>

                    {showAddDealModal && (
                      <EmployeeDirectoryAddUpdate
                        open={showAddDealModal}
                        onClose={() => {
                          setShowAddDealModal(false);
                        }}
                        data={{ contactid: contact?.id }}
                        tabIndex={1}
                        contactId={contact?.id}
                        onDealUpdated={() => GetData(contact?.id)} // refresh after update
                        onSubmit={onSuccess}
                        onError={onError}
                        type="account"
                      />
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <IdCard className="size-4" /> ID {contact?.id}
                    </div>
                  </div>
                </>
              ) : (
                <InnerLoader wrapperClass={"w-full"} />
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={alertData?.isShow}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>

      {invoicePopup && (
        <InvoiceQuickView
          onClose={() => {
            setInvoicePopup(false);
            setInvoiceData(null);
            setInvoiceProjectKey(null)
          }}
          data={invoiceData}
          projectkey={invoiceProjectKey}
          type="project"
        />
      )}

      {showActivityModal && (
        <AddActivity
          onSubmit={onActivitySuccess}
          onClose={() => {
            setShowActivityModal(false);
          }}
          onError={onError}
          UsersList={usersList}
          CustomerData={null}
          id={contact?.id}
          entitytype="contact"
          entityname={contact.name}
        />
      )}
    </>
  );
}

function QuickAction({ icon: Icon, label, href, onClick }) {
  const className =
    "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
  if (href)
    return (
      <a className={className} href={href}>
        <Icon className="size-4" /> {label}
      </a>
    );
  return (
    <button className={className} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}

function DealCard({
  deal,
  userid,
  onStatusChange,
  DealStages = [],
  onSubmit = () => { },
  onError = () => { },
}) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [wonFormDeal, setWonFormDeal] = useState(null);
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostFormDeal, setLostFormDeal] = useState(null);
  const [localStage, setLocalStage] = useState(
    deal?.current_stage?.dealstatus ?? "-",
  );

  const apiCall = useApiCall();

  const handleSuccess = (message) => {
    setShowWonModal(false);
    setShowLostModal(false);
    setShowEditModal(false);
    onSubmit(message);
    setEmpPopup(false);
    setEmpData({});
  };

  const handleError = (message) => {
    if (onError) onError(message);
  };

  const metas = Object.fromEntries(
    deal?.metas?.map((m) => [m.meta_key, m.meta_value]) ?? [],
  );

  const isWon = deal?.current_stage?.dealstatus === "Won";
  const isLost = deal?.current_stage?.dealstatus === "Lost";
  const currentStageIndex = DealStages.findIndex(
    (s) => s.name === deal?.current_stage?.dealstatus,
  );
  const activeColor = isWon ? "#22c55e" : isLost ? "#ef4444" : "#2563eb";
  const isWonOrLost = isWon || isLost;

  const [empPopup, setEmpPopup] = useState(false);
  const [empData, setEmpData] = useState({});

  const formatStageKey = (stage) => {
    return stage.toLowerCase().replace(/\s+/g, "_"); // "In Progress" -> "in_progress"
  };

  // --- HANDLE STAGE CHANGE ---
  const handleStageChange = async (newStage) => {
    const lowerStage = formatStageKey(newStage); // lowercase + underscores
    const currentStage = deal?.current_stage?.dealstatus ?? "-"; // ✅ added

    if (lowerStage === "won") {
      setWonFormDeal(deal);
      setShowWonModal(true);
      return;
    }

    if (lowerStage === "lost") {
      setLostFormDeal({ dealkey: deal.dealkey, name: deal.name });
      setShowLostModal(true);
      return;
    }

    const resultSwal = await Swal.fire({
      title: `Move deal "${deal.name}"?`,
      text: `Move from "${currentStage}" to "${newStage}"?`, // ✅ fixed reference
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      reverseButtons: true,
    });

    if (resultSwal.isConfirmed) {
      try {
        await apiCall({
          method: "post",
          url: "deals/update-deal-status",
          data: { dealkey: deal.dealkey, dealstatus: lowerStage },
        });

        // **Update card immediately without reload**
        onStatusChange(deal.dealid, newStage); // ✅ keep `newStage` or `lowerStage` depending on your display logic

        Swal.fire("Success!", `Deal moved to "${newStage}"`, "success");
      } catch (err) {
        Swal.fire(
          "Error",
          err?.response?.data?.message || "Something went wrong",
          "error",
        );
      }
    }
  };

  return (
    <>
      <div
        className="relative rounded-xl border border-slate-200 bg-white px-5 py-4 
             shadow-sm hover:shadow-md transition-all duration-300 
             space-y-3 text-sm w-full cursor-pointer hover:-translate-y-[1px] mb-[12px]"
      >
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex flex-col">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2 text-[15px]">
              <Briefcase className="size-4 text-blue-600" />
              {deal?.name ?? "Untitled Deal"}{" "}
              <span className="text-xs text-slate-500">#{deal?.dealid}</span>
            </h3>
          </div>

          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
            {metas?.dealamount ? `$${metas?.dealamount}` : "—"}
          </span>
        </div>

        {/* Stage Pipeline */}
        <div className="mt-2">
          <p className="text-xs text-slate-500 mb-1 font-medium">
            Stage Pipeline
          </p>
          <div className="flex w-full h-7 overflow-hidden rounded-md">
            {DealStages.map((s, i) => {
              const isActive = !isWonOrLost && i <= currentStageIndex;
              const isCurrent = i === currentStageIndex;
              const isFirst = i === 0;
              const isLast = i === DealStages.length - 1;

              let clipPath = "";
              if (isFirst) {
                clipPath =
                  "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)";
              } else if (isLast) {
                clipPath = "polygon(0 0, 10px 50%, 0 100%, 100% 100%, 100% 0)";
              } else {
                clipPath =
                  "polygon(0 0, 10px 50%, 0 100%, calc(100% - 10px) 100%, 100% 50%, calc(100% - 10px) 0)";
              }

              return (
                <div
                  key={s.id}
                  onClick={() =>
                    !isWonOrLost && !isCurrent && handleStageChange(s.name)
                  } // s.name will automatically go lowercase in handleStageChange
                  title={s.name}
                  style={{
                    clipPath,
                    WebkitClipPath: clipPath,
                    backgroundColor:
                      i <= currentStageIndex && !isWonOrLost
                        ? activeColor
                        : "#e5e7eb",
                    transition: "background-color 0.3s ease",
                    cursor:
                      isWonOrLost || isCurrent ? "not-allowed" : "pointer",
                  }}
                  className="flex-1 relative flex items-center justify-center text-[10px] font-medium text-white select-none"
                >
                  {i <= currentStageIndex && !isWonOrLost ? (
                    <Check className="size-3" />
                  ) : (
                    s.name[0]
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions Row */}
        <div className="flex justify-between items-center mt-4">
          {/* Won/Lost Badge */}

          <div className="flex gap-2">
            <div>
              {(isWon || isLost) && (
                <div
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${isWon
                    ? "bg-green-100 text-green-700"
                    : "bg-rose-100 text-rose-700"
                    }`}
                >
                  {isWon ? (
                    <Trophy className="size-3" />
                  ) : (
                    <XCircle className="size-3" />
                  )}
                  {isWon ? "Won" : "Lost"}
                </div>
              )}
              {!isWon && !isLost && <div className="h-[22px]" />}
            </div>

            <div>
              {(metas?.is_addon == 1 || metas?.is_addon === "1") && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700">
                  <Puzzle className="size-3" />{" "}
                  {/* or <PlusCircle className="size-3" /> */}
                  Addon
                </div>
              )}
            </div>
          </div>

          {/* Action Icons */}
          <div className="flex items-center gap-3">
            {/* <button
              onClick={() => {
                setEmpPopup(true);
                setEmpData(deal);
              }}
              title="Quick View"
              className="p-2 rounded-full bg-slate-100 hover:bg-blue-100 hover:text-blue-600 text-slate-500 transition"
            >
              <Eye className="size-4" />
            </button> */}
            <Link
              to={`/lead-account/detail/${userid}/deal/${deal?.dealid}`}
              target="_blank"
              className={
                "p-2 rounded-lg  hover:text-[#34C759] hover:bg-slate-100 text-slate-600 transition "
              }
            >
              <div className="flex max-w-4 max-h-4 ">
                <NewCardIcon className="h-4.5 w-4.5" />
              </div>
            </Link>

            <button
              onClick={() => !isWonOrLost && setShowEditModal(true)}
              className={`p-2 rounded-lg hover:text-[#007AFF] hover:bg-slate-100 text-slate-600 transition ${isWonOrLost ? "opacity-40 cursor-not-allowed" : ""
                }`}
              title="Edit Deal"
              disabled={isWonOrLost} // <- button ko bhi disable kar diya
            >
              <Pencil size={16} />
            </button>

            <button
              onClick={() => !isWonOrLost && handleStageChange("Won")}
              title="Mark as Won"
              className={`p-2 rounded-full transition ${isWonOrLost ? "cursor-not-allowed" : "cursor-pointer"} ${isWon
                ? "bg-green-500 text-white"
                : "bg-slate-100 text-slate-500 hover:bg-green-100 hover:text-green-600"
                }`}
            >
              <ThumbsUp className="size-4" />
            </button>

            <button
              onClick={() => !isWonOrLost && handleStageChange("Lost")}
              title="Mark as Lost"
              className={`p-2 rounded-full transition  ${isWonOrLost ? "cursor-not-allowed" : "cursor-pointer"} ${isLost
                ? "bg-rose-500 text-white "
                : "bg-slate-100 text-slate-500 hover:bg-rose-100 hover:text-rose-600"
                }`}
            >
              <ThumbsDown className="size-4" />
            </button>
          </div>
        </div>
      </div>

      {showEditModal && (
        <EmployeeDirectoryAddUpdate
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
          }}
          data={deal}
          tabIndex={1}
          contactId={userid}
          onDealUpdated={() => onStatusChange(deal.dealid)} // refresh after update
          onSubmit={handleSuccess}
          onError={handleError}
          type="account"
        />
      )}

      {/* Won Deal Modal */}
      {showWonModal && (
        <WonDeal
          wonFormDeal={wonFormDeal}
          onClose={() => setShowWonModal(false)}
          onDealUpdated={() => onStatusChange(deal.dealid)}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}

      {showLostModal && (
        <LostDeal
          lostFormDeal={lostFormDeal}
          onClose={() => setShowLostModal(false)}
          onSubmit={handleSuccess}
          onError={handleError}
        />
      )}

      {empPopup && (
        <DealQuickView
          onClose={() => {
            setEmpPopup(false);
            setEmpData({});
          }}
          data={empData}
          DealStages={DealStages}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      )}
    </>
  );
}

function TabPlaceholder({ icon: Icon, title, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-300 rounded-2xl bg-white">
      <div className="flex items-center justify-center size-14 rounded-full bg-slate-100 mb-4">
        <Icon className="size-6 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{message}</p>
      <div className="mt-4 px-4 py-2 text-xs text-slate-400 border border-solid border-slate-200 rounded-full bg-slate-50">
        🚧 Under Development
      </div>
    </div>
  );
}
